import { InvoiceType } from './accountsReceivable.types';

export const ClientResponseType = {
  INDIVIDUAL: 'INDIVIDUAL',
  FIRM: 'FIRM',
} as const;
export type ClientResponseType = (typeof ClientResponseType)[keyof typeof ClientResponseType];

export type ClientStatus = 'ACTIVE' | 'PENDING' | 'INACTIVE' | 'EXPIRED';

export const ContactResponseType = {
  LAWYER: 'LAWYER',
  STAFF: 'STAFF',
} as const;
export type ContactResponseType = (typeof ContactResponseType)[keyof typeof ContactResponseType];
// Alias kept for call sites that used the SDK's name.
export const ContactResponseContactType = ContactResponseType;
export type ContactResponseContactType = ContactResponseType;

export type ContactEmailStatus = 'EACH' | 'COMPLETED' | 'NONE';
// Expected values are ContactEmailStatus, but the API types the field as any string; kept loose to match.
export type ContactStatusUpdateRequestEmailStatus = string | null;

export interface ContactResponse {
  cell?: string | null;
  clientId?: string;
  contactId?: string;
  createdAt: string | null;
  createdById?: string | null;
  deletedAt: string | null;
  email: string;
  emailStatus?: ContactEmailStatus;
  extension?: string | null;
  fax?: string | null;
  firstName?: string;
  lastName?: string;
  lawyerBarId?: string | null;
  phone?: string | null;
  type?: ContactResponseType;
  updatedAt: string | null;
}

interface ContactWriteFields {
  cell?: string | null;
  clientId?: string | null;
  createdById?: string | null;
  email: string;
  emailStatus?: ContactEmailStatus | null;
  extension?: string | null;
  fax?: string | null;
  firstName: string;
  lastName: string;
  lawyerBarId?: string | null;
  phone?: string | null;
  type: string | null;
}

export type ContactCreateRequest = ContactWriteFields;

export interface ContactUpdateRequest extends ContactWriteFields {
  contactId?: string | null;
}

export type ContactGetAllParams = {
  clientId?: string;
  firstName?: string;
  lastName?: string;
  phone?: string;
  type?: string;
  pageNo?: number | string;
  pageSize?: number | string;
  sortField?: 'firstName' | 'lastName' | 'phone' | 'type';
  sortDirection?: 'asc' | 'desc';
  search?: string;
};

export interface ContactGetAllResponse {
  contacts: ContactResponse[];
  currentPage: number;
  totalPages: number;
  totalRows: number;
}

export interface ContactStatusUpdateRequest {
  email?: string;
  emailStatus?: ContactStatusUpdateRequestEmailStatus;
  sendAffidavit?: boolean;
}

/** Email-distribution lists shared by the client response and write requests. */
interface ClientEmailLists {
  affidavit?: string[];
  affidavitCc?: string[];
  affidavitWithInvoice?: string[];
  affidavitWithInvoiceCc?: string[];
  arInvoices?: string[];
  arInvoicesCc?: string[];
  finalBillingSummary?: string[];
  finalBillingSummaryCc?: string[];
  recapAndInvoice?: string[];
  recapAndInvoiceCc?: string[];
  statement?: string[];
  statementCc?: string[];
}

export interface ClientResponse extends ClientEmailLists {
  activatedAt: string | null;
  address1?: string | null;
  address2?: string | null;
  canAffidavitWithInvoice?: boolean | null;
  city?: string | null;
  clientId: string;
  clientName?: string | null;
  code: string;
  contacts?: ContactResponse[];
  createdAt: string | null;
  createdById?: string | null;
  deactivatedAt: string | null;
  deletedAt: string | null;
  fax?: string | null;
  firstName?: string | null;
  instructions?: string | null;
  lastName?: string | null;
  phone?: string | null;
  state?: string | null;
  status?: ClientStatus;
  type: ClientResponseType;
  updatedAt: string | null;
  website?: string | null;
  zipCode?: string | null;
}

/** Full GET /clients/{id} response. See ClientByIdResponse below for the subset some flows use. */
export type ClientGetByIdResponse = ClientResponse;

export type ClientGetAllParams = {
  pageNo?: number;
  pageSize?: number;
  sortField?: 'code' | 'clientName' | 'city' | 'state' | 'zipCode' | 'createdAt' | 'updatedAt';
  sortDirection?: 'asc' | 'desc';
  search?: string;
  type?: ClientResponseType;
  name?: string;
  code?: string;
};

export interface ClientGetAllResponse {
  clients: ClientResponse[];
  currentPage: number;
  totalPages: number;
  totalRows: number;
}

interface ClientWriteFields extends ClientEmailLists {
  address1: string;
  address2?: string | null;
  canAffidavitWithInvoice?: boolean | null;
  city: string;
  clientName?: string | null;
  createdAt?: string | null;
  createdById?: string | null;
  deletedAt?: string | null;
  fax?: string | null;
  firstName?: string | null;
  instructions?: string | null;
  lastName?: string | null;
  phone?: string | null;
  state: string;
  type: string | null;
  updatedAt?: string | null;
  website?: string | null;
  zipCode: string;
}

export interface ClientCreateRequest extends ClientWriteFields {
  contacts?: ContactCreateRequest[];
}

export interface ClientUpdateRequest extends ClientWriteFields {
  contacts: ContactUpdateRequest[];
}

export interface ClientUpdateStatusRequest {
  status: boolean;
}

export interface ClientUpdateStatusResponse {
  activatedAt: string | null;
  deactivatedAt: string | null;
  message: string;
  status?: ClientStatus;
}

export interface ClientCsvResponse {
  downloadUrl: string;
}

export enum RecapFileType {
  PDF = 'PDF',
  CSV = 'CSV',
  BOTH = 'BOTH',
}

export enum RecapFrequency {
  WEEKLY = 'WEEKLY',
  NIGHTLY = 'NIGHTLY',
}

export enum StatementFileType {
  PDF = 'PDF',
  CSV = 'CSV',
  BOTH = 'BOTH',
}

export enum FbsFileType {
  PDF = 'PDF',
  CSV = 'CSV',
  BOTH = 'BOTH',
}

export type BillingPreferenceResponse = {
  billingPreferenceId?: string;
  billingType?: InvoiceType;
  isArRequired?: boolean;
  isInvoiceRequired?: boolean;
  isRecapRequired?: boolean;
  recapFileType?: RecapFileType;
  recapFrequency?: RecapFrequency;
  statementFileType?: StatementFileType;
  applyFinancialCharges?: boolean;
  isFbsRequired?: boolean;
  fbsFileType?: FbsFileType;
  createdAt?: Date;
  client?: {
    type: ClientResponseType;
    firstName?: string;
    lastName?: string;
    clientName?: string;
  } | null;
};

export type BillingPreferenceUpdateSchema = {
  billingType?: InvoiceType;
  isArRequired?: boolean;
  isInvoiceRequired?: boolean;
  isRecapRequired?: boolean;
  recapFileType?: RecapFileType | null;
  recapFrequency?: RecapFrequency | null;
  statementFileType?: StatementFileType | null;
  applyFinancialCharges?: boolean;
  isFbsRequired?: boolean;
  fbsFileType?: FbsFileType | null;
};

/** Subset of GET /clients/{id} (`ClientGetByIdResponse`) used by create/edit and invoice-share flows. */
export type ClientByIdResponse = {
  clientId: string;
  code: string;
  clientName: string;
  status?: string;
  type?: ClientResponseType;
  firstName?: string | null;
  lastName?: string | null;
  /** A/R invoice recipient emails (OpenAPI ClientResponse.arInvoices). */
  arInvoices?: string[] | null;
  /** A/R invoice CC emails (OpenAPI ClientResponse.arInvoicesCc). */
  arInvoicesCc?: string[] | null;
  /** Affidavit-with-invoice recipient emails (OpenAPI ClientResponse.affidavitWithInvoice). */
  affidavitWithInvoice?: string[] | null;
  /** Affidavit-with-invoice CC emails (OpenAPI ClientResponse.affidavitWithInvoiceCc). */
  affidavitWithInvoiceCc?: string[] | null;
};
